import React from "react";
import { 
  UserPlus, 
  ShieldCheck, 
  Users, 
  Mail, 
  KeyRound,
  ArrowRight,
  Trophy
} from "lucide-react";

const RegisterPage = () => {
  return (
    <div className="space-y-12 pb-20 max-w-3xl mx-auto">
      {/* Hero Header */}
      <section className="text-center md:text-left">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-100 mb-4">
          <Trophy size={14} className="text-blue-600" />
          <span className="text-[10px] font-black text-blue-600 uppercase tracking-widest">Join Nuvio Sport</span>
        </div>
        <h1 className="text-4xl font-black text-gray-900 tracking-tight mb-4">Create Your Profile</h1>
        <p className="text-lg text-gray-600 leading-relaxed">
          Welcome to the starting line. Creating your account is the first step toward tracking 
          your progress and getting on the radar of elite coaches.
        </p>
      </section>

      {/* 1. Selecting the Right Role */}
      <section className="grid md:grid-cols-2 gap-8 items-center bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="bg-blue-100 p-2 rounded-lg text-blue-600"><Users size={20}/></div>
            <h3 className="text-xl font-bold text-gray-900">Choose Your Role</h3>
          </div>
          <p className="text-gray-600 text-sm leading-relaxed">
            Nuvio is built for everyone in the sports ecosystem. During registration, you'll pick the role that fits you best:
          </p>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex gap-2"><strong>• Athlete:</strong> Focus on training and scores.</li>
            <li className="flex gap-2"><strong>• Coach:</strong> Focus on scouting and rosters.</li>
            <li className="flex gap-2"><strong>• Trainer:</strong> Focus on skill development.</li>
          </ul>
        </div>
        <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100 text-center">
           <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Pro Tip</p>
           <p className="text-sm text-gray-700 italic">"Make sure you select the correct role—this unlocks your specific dashboard and training tools."</p>
        </div>
      </section>

      {/* 2. Simple Steps to Get In */}
      <section className="space-y-6">
        <h3 className="text-xl font-bold text-gray-900 px-1">How it works</h3>
        <div className="grid md:grid-cols-3 gap-4">
           {/* Step 1 */}
           <div className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm">
              <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center font-black mb-4">1</div>
              <p className="font-bold text-gray-900 mb-1 text-sm text-left">Register</p>
              <p className="text-xs text-gray-500 text-left">Fill out your name, email, and choose a secure password.</p>
           </div>
           {/* Step 2 */}
           <div className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm">
              <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center font-black mb-4">2</div>
              <p className="font-bold text-gray-900 mb-1 text-sm text-left">Verify</p>
              <p className="text-xs text-gray-500 text-left">Check your email for a confirmation link to keep your data safe.</p>
           </div>
           {/* Step 3 */}
           <div className="p-6 bg-blue-600 text-white rounded-2xl shadow-lg shadow-blue-200">
              <div className="w-10 h-10 bg-white/20 text-white rounded-full flex items-center justify-center font-black mb-4">3</div>
              <p className="font-bold mb-1 text-sm text-left">Level Up</p>
              <p className="text-xs text-blue-100 text-left">Log in to start Phase 2 and build your athletic identity.</p>
           </div>
        </div>
      </section>

      {/* 3. Privacy & Security */}
      <section className="bg-slate-900 rounded-3xl p-8 text-white">
        <div className="flex flex-col md:flex-row gap-8 items-center">
          <div className="flex-1 space-y-4">
            <div className="flex items-center gap-2">
              <ShieldCheck className="text-green-400" />
              <h3 className="text-2xl font-bold">Your Data is Secure</h3>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">
              We use industry-standard encryption to protect your performance stats and personal info. 
              Only the coaches you follow can see your detailed scores.
            </p>
          </div>
          <div className="flex gap-4">
             <Mail size={32} className="text-slate-700" />
             <KeyRound size={32} className="text-slate-700" />
          </div>
        </div>
      </section>

      {/* Footer Navigation */}
      <div className="flex justify-center items-center gap-4 text-xs font-bold uppercase tracking-widest text-gray-400 pt-8">
        <span>Register</span>
        <ArrowRight size={14} />
        <span>Login</span>
        <ArrowRight size={14} />
        <span className="text-blue-600">Onboarding Checklist</span>
      </div>
    </div>
  );
};

export default RegisterPage;